/* Dashboard view */
import { type FC, useEffect, useState } from "react";

import { whForMonth } from "@shared/allocation";
import type { ActualsByProject, CostsByPerson, Person, Project } from "@shared/types";
import { allocToHours, downloadCsv, getActualHours, getPerson, rateForMemberAt } from "../data";
import {
  type DashboardFinanceSummary,
  type DashboardProjectCard,
  type DashboardUtilisationRow,
  getDashboardFinanceSummary,
  getDashboardProjectsSummary,
  getDashboardUtilisation,
} from "../lib/dashboard-api";
import { useScrollCurrentIntoView, windowMonths } from "../lib/grid-helpers";
import { extrasForYmKey } from "../lib/holiday-cache";
import type { SpaMode } from "../lib/me-api";
import { useC } from "../theme";
import type { MonthEntry } from "../tokens";
import { Av, Btn, Card, SBadge, SortableHeader, useTableSort, useTableStyles } from "../ui";

type DashboardAlert =
  | { type: "over"; person: Person; month: MonthEntry; value: number }
  | {
      type: "under";
      person: Person;
      month: MonthEntry;
      value: number;
      count: number;
      cap: number;
    }
  | { type: "unassigned"; project: Project; count: number };

interface KpiCard {
  l: string;
  v: string | number;
  sub: string;
  co: string;
  bg: string;
  bd: string;
  go: () => void;
}

export interface DashboardViewProps {
  projects: Project[];
  people: Person[];
  costs: CostsByPerson;
  mode: SpaMode;
  actuals: ActualsByProject;
  rangeStart: string;
  rangeEnd: string;
  role: string;
  currentUserId: number | null;
  setView: (v: string) => void;
  setSelectedProjectIds: (ids: number[]) => void;
  openPerson?: (pid: number) => void;
}

export const DashboardView: FC<DashboardViewProps> = ({
  projects,
  people,
  mode,
  actuals,
  rangeStart,
  rangeEnd,
  role,
  currentUserId,
  setView,
  setSelectedProjectIds,
  openPerson,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const months = windowMonths(rangeStart, rangeEnd);
  const isActualMode = mode === "actuals";
  const canSeeFinance = role === "admin" || role === "lead";
  useScrollCurrentIntoView(rangeStart, rangeEnd, "dashboard");

  // /api/projects returns the full portfolio to PMs (with masked rate
  // fields per projects spec); the dashboard tiles must reflect the PM's
  // own scope, not company-wide. Filter client-side by pmId here.
  const inScope = (p: Project) => role !== "pm" || p.pmId === currentUserId;
  const activeProjects = projects.filter((p) => p.status === "active" && inScope(p));
  const plannedProjects = projects.filter((p) => p.status === "planned" && inScope(p));
  let totalHours = 0;
  let unassignedCount = 0;
  for (const p of activeProjects) {
    /* Time-off projects are non-billable — they contribute no "Planned Hours"
       to the dashboard KPIs. Finance totals are API-owned below. */
    if (p.isTimeOff) continue;
    for (const m of p.members) {
      const pers = m.pid ? getPerson(m.pid, people) : null;
      const pObj = { wl: pers?.wl || 100 } as Person;
      if (isActualMode && m.pid) {
        const projActuals = actuals[p.id];
        const monthly = projActuals?.[m.pid] || {};
        for (const h of Object.values(monthly)) {
          totalHours += h;
        }
      } else {
        for (const [ym, a] of Object.entries(m.a)) {
          const h = allocToHours(a, pObj, ym, extrasForYmKey(ym));
          totalHours += h;
        }
      }
      if (!m.pid) unassignedCount++;
    }
  }

  /* Server-driven utilisation strip + projects summary. Range/mode are read
     server-side from the caller's userPrefs; we re-fetch when they change so
     the dashboard reflects the picker. The accountant role gets 403 from
     these endpoints (its dashboard tab is gated upstream); we swallow the
     error and render an empty heatmap. */
  const [utilRows, setUtilRows] = useState<DashboardUtilisationRow[]>([]);
  const [summaryCards, setSummaryCards] = useState<DashboardProjectCard[]>([]);
  const [financeSummary, setFinanceSummary] = useState<DashboardFinanceSummary | null>(null);
  // mode/range are read server-side from userPrefs, but we depend on them
  // here so the dashboard re-fetches when the picker writes a new window.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps drive refetch
  useEffect(() => {
    let cancelled = false;
    const isAccountant = role === "accountant";
    // Accountant is gated upstream — the 403 from these endpoints lands as
    // an empty heatmap. Any other error path also degrades to empty rather
    // than throwing a render-time error, matching the existing me-api /
    // costs-api behaviour for transient backend failures.
    const utilP = isAccountant
      ? Promise.resolve<DashboardUtilisationRow[]>([])
      : getDashboardUtilisation().catch((): DashboardUtilisationRow[] => []);
    const sumP = isAccountant
      ? Promise.resolve<DashboardProjectCard[]>([])
      : getDashboardProjectsSummary().catch((): DashboardProjectCard[] => []);
    const financeP = canSeeFinance
      ? getDashboardFinanceSummary().catch((): DashboardFinanceSummary | null => null)
      : Promise.resolve<DashboardFinanceSummary | null>(null);
    void Promise.all([utilP, sumP, financeP]).then(([u, s, f]) => {
      if (cancelled) return;
      setUtilRows(u);
      setSummaryCards(s);
      setFinanceSummary(f);
    });
    return () => {
      cancelled = true;
    };
  }, [role, mode, rangeStart, rangeEnd, canSeeFinance]);

  /* `myProjectIds` is still used by the unassigned-role alert below. */
  const myProjectIds = new Set(
    role === "pm" && currentUserId
      ? projects.filter((p) => p.pmId === currentUserId).map((p) => p.id)
      : projects.map((p) => p.id),
  );

  /* peopleHeatmap shape preserves the renderer contract: each row carries a
     person reference and a per-month integer percent map. We adapt server
     rows into that shape, falling back to the people prop for fields the
     server does not serialise. Planned/actual split is gone — the server
     already mode-honours via stored userPrefs. */
  const peopleHeatmap = utilRows
    .map((row) => {
      const fallback = people.find((p) => p.id === row.personId);
      const person: Person = fallback ?? {
        id: row.personId,
        name: row.name,
        email: "",
        sysRole: null,
        emp: "full",
        wl: row.wl,
        roles: [],
        periods: [],
        status: "active",
        excludeFromPlanning: false,
      };
      return { person, util: row.utilisation };
    })
    // Archived people only render in the heatmap when they have data inside
    // the visible window; otherwise they're noise.
    .filter(({ person, util }) => {
      if (person.excludeFromPlanning) return false;
      if (person.status !== "archived") return true;
      return months.some((m) => (util[m.key] || 0) > 0);
    });

  const alerts: DashboardAlert[] = [];
  /* Alerts.  Over-planned: month allocation exceeds person's capacity by >5%.
     Under-planned: month allocation <50% of capacity, but only for months
     starting at the current month onward (no point flagging the past).
     We also aggregate per-person under-allocation: rather than one row per
     month, collapse a run of quiet months into a single "under-planned for
     N months starting M" entry so the panel stays scannable. */
  const currentIdx = months.findIndex((m) => m.isCurrent);
  const futureStart = currentIdx < 0 ? 0 : currentIdx;

  // Cells are wl-normalised per ADR-0001, so the over/under thresholds are
  // FTE-relative (100 = full capacity) regardless of the person's wl.
  const FTE = 100;
  for (const { person, util } of peopleHeatmap) {
    // Archived people are never surfaced in Attention Required — they're
    // off the roster and not actionable.
    if (person.status === "archived") continue;
    for (const m of months) {
      const t = util[m.key] || 0;
      if (t > FTE + 5) {
        alerts.push({ type: "over", person, month: m, value: Math.round(t) });
      }
    }
    /* Under-planned detection (current + future months only) */
    const underMonths: { m: MonthEntry; value: number }[] = [];
    for (let i = futureStart; i < months.length; i++) {
      const m = months[i];
      if (!m) continue;
      const t = util[m.key] || 0;
      if (t < FTE * 0.5) underMonths.push({ m, value: Math.round(t) });
    }
    const head = underMonths[0];
    if (head) {
      /* Collapse to a single alert — first under-month + count */
      alerts.push({
        type: "under",
        person,
        month: head.m,
        value: head.value,
        count: underMonths.length,
        cap: person.wl || 100,
      });
    }
  }
  for (const p of projects) {
    if (p.status === "archived") continue;
    if (role === "pm" && !myProjectIds.has(p.id)) continue;
    const un = p.members.filter((m) => !m.pid).length;
    if (un > 0) alerts.push({ type: "unassigned", project: p, count: un });
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1600, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: C.navyDeep,
            margin: 0,
            fontFamily: "'Poppins',system-ui,sans-serif",
            letterSpacing: -0.3,
          }}
        >
          Dashboard
        </h2>
        <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
          Company-wide snapshot · {months.length}-month focus window ·{" "}
          {isActualMode ? "actuals" : "planned"}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${canSeeFinance && financeSummary ? 4 : 2},1fr)`,
          gap: 12,
          marginBottom: 18,
        }}
      >
        {(
          [
            /* "Active Projects" / "Planned Hours" drill into the Projects VIEW
           (the planning grid with all active projects pre-selected), not
           Project Setup (that's the admin config screen). */
            {
              l: "Active Projects",
              v: activeProjects.length,
              sub: `${plannedProjects.length} planned`,
              co: C.blue,
              bg: C.blueTint2,
              bd: C.blueTint4,
              go: () => {
                setSelectedProjectIds(activeProjects.map((p) => p.id));
                setView("project_detail");
              },
            },
            {
              l: isActualMode ? "Logged Hours" : "Planned Hours",
              v: `${Math.round(totalHours).toLocaleString()}h`,
              sub: `${months.length} months window`,
              co: C.navy,
              bg: C.canvas,
              bd: C.line,
              go: () => {
                setSelectedProjectIds(activeProjects.map((p) => p.id));
                setView("project_detail");
              },
            },
            /* Revenue + Margin are API-owned finance values. PMs and accountants
           do not receive this endpoint's cost-bearing DTO. */
            canSeeFinance &&
              financeSummary && {
                l: isActualMode ? "Revenue Realised" : "Revenue Prognosis",
                v: `€${Math.round(financeSummary.revenue / 1000)}k`,
                sub: `cost €${Math.round(financeSummary.cost / 1000)}k`,
                co: C.blue,
                bg: C.blueTint2,
                bd: C.blueTint4,
                go: () => setView("budget"),
              },
            canSeeFinance &&
              financeSummary && {
                l: "Margin",
                v: `${financeSummary.marginPct}%`,
                sub: `€${Math.round(financeSummary.margin / 1000)}k`,
                co: financeSummary.marginPct >= 20 ? C.g : C.y,
                bg: financeSummary.marginPct >= 20 ? C.gBg : C.yBg,
                bd: financeSummary.marginPct >= 20 ? C.gBd : C.yBd,
                go: () => setView("budget"),
              },
          ] as Array<KpiCard | false | null>
        )
          .filter((k): k is KpiCard => k !== false && k !== null)
          .map((k) => (
            <Card
              key={k.l}
              pad="14px 18px"
              hover
              onClick={k.go}
              style={{ background: k.bg, borderColor: k.bd, cursor: "pointer" }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: C.slateMut,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 3,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{k.l}</span>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  style={{ opacity: 0.5 }}
                  aria-hidden="true"
                >
                  <path
                    d="M4 2l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: k.co,
                  fontVariantNumeric: "tabular-nums",
                  fontFamily: "'Poppins',system-ui,sans-serif",
                  letterSpacing: -0.5,
                  lineHeight: 1.1,
                }}
              >
                {k.v}
              </div>
              {k.sub && (
                <div
                  style={{
                    fontSize: 11,
                    color: C.slateMut,
                    marginTop: 3,
                    fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  }}
                >
                  {k.sub}
                </div>
              )}
            </Card>
          ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: "14px 18px",
              borderBottom: `1px solid ${C.lineSoft}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.navyDeep,
                  fontFamily: "'Poppins',system-ui,sans-serif",
                }}
              >
                Team Capacity Heatmap
              </div>
              <div style={{ fontSize: 11.5, color: C.slateMut, marginTop: 2 }}>
                {isActualMode ? "Logged" : "Planned"} % of capacity · {months.length} months
              </div>
            </div>
            <Btn small onClick={() => setView("person_detail")}>
              Open Timeline →
            </Btn>
          </div>
          <div style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      ...TH,
                      textAlign: "left",
                      minWidth: 150,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      background: C.canvas,
                      zIndex: 3,
                    }}
                  >
                    Person
                  </th>
                  {months.map((m) => {
                    const isCurrent = m.isCurrent;
                    return (
                      <th
                        key={m.key}
                        data-current-month={isCurrent ? "true" : undefined}
                        style={{
                          ...TH,
                          textAlign: "center",
                          minWidth: 46,
                          padding: "8px 4px",
                          fontSize: 10,
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          background: isCurrent ? C.blueTint2 : C.canvas,
                          borderBottom: `1px solid ${C.line}`,
                          boxShadow: isCurrent ? `inset 0 -2px 0 ${C.blue}` : undefined,
                        }}
                      >
                        {m.short}
                        <br />
                        <span style={{ fontSize: 8.5, color: C.slateDim, fontWeight: 500 }}>
                          {String(m.year).slice(2)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {peopleHeatmap.map(({ person, util }, i) => {
                  const rowBg = i % 2 ? C.surfaceAlt : C.surface;
                  return (
                    <tr key={person.id}>
                      <td
                        style={{
                          ...TD,
                          textAlign: "left",
                          position: "sticky",
                          left: 0,
                          background: rowBg,
                          zIndex: 1,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openPerson?.(person.id)}
                          title="Open in Person view"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            textAlign: "left",
                            width: "100%",
                          }}
                        >
                          <Av name={person.name} sz={22} />
                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.navyDeep,
                                textDecoration: "underline",
                                textDecorationColor: "transparent",
                                textUnderlineOffset: 2,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.textDecorationColor = C.blue;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.textDecorationColor = "transparent";
                              }}
                            >
                              {person.name}
                            </div>
                            <div style={{ fontSize: 10, color: C.slateMut }}>{person.wl}% cap</div>
                          </div>
                        </button>
                      </td>
                      {months.map((m) => {
                        const t = util[m.key] || 0;
                        // Cells are wl-normalised per ADR-0001, so 100 is the
                        // FTE threshold for over/under regardless of person.wl.
                        const FTE = 100;
                        let bg = C.lineSoft;
                        let co = C.slateDim;
                        let label = "·";
                        if (t > 0) {
                          label = `${Math.round(t)}%`;
                          /* heatmap intensity: blend the color toward more saturation */
                          if (t > FTE + 5) {
                            bg = C.rBg;
                            co = C.r;
                          } else if (t >= FTE - 5) {
                            bg = C.gBg;
                            co = C.g;
                          } else if (t >= FTE * 0.5) {
                            bg = C.blueTint2;
                            co = C.blue;
                          } else {
                            bg = C.yBg;
                            co = C.y;
                          }
                        }
                        return (
                          <td
                            key={m.key}
                            style={{
                              padding: "3px",
                              borderBottom: `1px solid ${C.lineSoft}`,
                              textAlign: "center",
                              background: m.isCurrent ? C.blueTint1 : "transparent",
                            }}
                          >
                            <div
                              style={{
                                background: bg,
                                color: co,
                                borderRadius: 3,
                                padding: "4px 2px",
                                fontSize: 10,
                                fontWeight: 600,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {label}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card style={{ overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.lineSoft}` }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.navyDeep,
                fontFamily: "'Poppins',system-ui,sans-serif",
              }}
            >
              Attention Required
            </div>
            <div style={{ fontSize: 11.5, color: C.slateMut, marginTop: 2 }}>
              {alerts.length} item{alerts.length === 1 ? "" : "s"}
            </div>
          </div>
          <div style={{ maxHeight: 380, overflow: "auto" }}>
            {alerts.length === 0 ? (
              <div
                style={{
                  padding: "32px 18px",
                  textAlign: "center",
                  color: C.slateMut,
                  fontSize: 12.5,
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  style={{ margin: "0 auto 8px", display: "block" }}
                  aria-hidden="true"
                >
                  <circle cx="16" cy="16" r="13" stroke={C.g} strokeWidth="2" />
                  <path
                    d="M10 16l4 4 8-8"
                    stroke={C.g}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                All clear
              </div>
            ) : (
              (() => {
                /* Sort: over-planned (red) first, then unassigned (yellow), then
               under-planned (blue-tinted).  Within a bucket, over is sorted
               by severity (higher % first), under by run-length (longer runs
               first), unassigned by count. */
                const sevRank: Record<DashboardAlert["type"], number> = {
                  over: 0,
                  unassigned: 1,
                  under: 2,
                };
                const sorted = [...alerts].sort((a, b) => {
                  const d = sevRank[a.type] - sevRank[b.type];
                  if (d) return d;
                  // Same type after rank tiebreak — narrow b to match.
                  if (a.type === "over" && b.type === "over") return b.value - a.value;
                  if (a.type === "unassigned" && b.type === "unassigned") return b.count - a.count;
                  if (a.type === "under" && b.type === "under") return b.count - a.count;
                  return 0;
                });
                return sorted.slice(0, 8).map((a) => {
                  const isOver = a.type === "over";
                  const isUnder = a.type === "under";
                  const isUnassigned = a.type === "unassigned";
                  const goTo = isOver
                    ? () => openPerson?.(a.person.id)
                    : isUnder
                      ? () => openPerson?.(a.person.id)
                      : () => {
                          setSelectedProjectIds([a.project.id]);
                          setView("project_detail");
                        };
                  const title =
                    isOver || isUnder
                      ? `Open ${a.person.name}'s timeline`
                      : `Open ${a.project.name} — assign roles`;
                  const dotColor = isOver ? C.r : isUnder ? C.blue : C.y;
                  const alertKey = isOver
                    ? `over-${a.person.id}-${a.month.key}`
                    : isUnder
                      ? `under-${a.person.id}-${a.month.key}`
                      : `unassigned-${a.project.id}`;
                  return (
                    <button
                      type="button"
                      key={alertKey}
                      onClick={goTo}
                      title={title}
                      style={{
                        width: "100%",
                        padding: "10px 16px",
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        background: "transparent",
                        border: "none",
                        borderBottom: `1px solid ${C.lineSoft}`,
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "background .1s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = C.canvas;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: dotColor,
                          marginTop: 6,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isOver && (
                          <>
                            <div style={{ fontSize: 12, color: C.navyDeep, fontWeight: 600 }}>
                              {a.person.name} over-planned
                            </div>
                            <div style={{ fontSize: 11, color: C.slateMut, marginTop: 1 }}>
                              {a.value}% in {a.month.label} · cap {a.person.wl}%
                            </div>
                          </>
                        )}
                        {isUnder && (
                          <>
                            <div style={{ fontSize: 12, color: C.navyDeep, fontWeight: 600 }}>
                              {a.person.name} under-planned
                            </div>
                            <div style={{ fontSize: 11, color: C.slateMut, marginTop: 1 }}>
                              {a.value}% in {a.month.label}
                              {a.count > 1
                                ? ` · ${a.count} month${a.count > 1 ? "s" : ""} below 50% cap`
                                : ` · cap ${a.cap}%`}
                            </div>
                          </>
                        )}
                        {isUnassigned && (
                          <>
                            <div style={{ fontSize: 12, color: C.navyDeep, fontWeight: 600 }}>
                              {a.project.name}
                            </div>
                            <div style={{ fontSize: 11, color: C.slateMut, marginTop: 1 }}>
                              {a.count} unassigned role{a.count > 1 ? "s" : ""}
                            </div>
                          </>
                        )}
                      </div>
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        style={{ marginTop: 4, color: C.slateMut, flexShrink: 0 }}
                        aria-hidden="true"
                      >
                        <path
                          d="M4 2l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </svg>
                    </button>
                  );
                });
              })()
            )}
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: C.navyDeep,
              fontFamily: "'Poppins',system-ui,sans-serif",
            }}
          >
            Projects
          </div>
          <Btn
            small
            onClick={() => {
              setSelectedProjectIds(projects.map((p) => p.id));
              setView("project_detail");
            }}
          >
            View all →
          </Btn>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {summaryCards.map((card) => {
            const pm = card.pmId != null ? getPerson(card.pmId, people) : undefined;
            const p = { id: card.id, name: card.name, code: card.code, status: card.status };
            return (
              <Card
                key={card.id}
                hover
                pad="14px 16px"
                onClick={() => {
                  setSelectedProjectIds([card.id]);
                  setView("project_detail");
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.navyDeep,
                        marginBottom: 2,
                        fontFamily: "'Poppins',system-ui,sans-serif",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: C.slateMut }}>{p.code}</div>
                  </div>
                  <SBadge s={p.status} />
                </div>
                <div style={{ display: "flex", gap: 14, fontSize: 11, color: C.slateMut }}>
                  <div>
                    <div
                      style={{
                        color: C.navyDeep,
                        fontWeight: 700,
                        fontSize: 14,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      €{(card.budget / 1000).toFixed(0)}k
                    </div>
                    budget
                  </div>
                  <div>
                    <div
                      style={{
                        color: C.navyDeep,
                        fontWeight: 700,
                        fontSize: 14,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {card.teamCount}
                    </div>
                    team
                  </div>
                  <div>
                    <div
                      style={{
                        color: card.unassignedCount ? C.r : C.g,
                        fontWeight: 700,
                        fontSize: 14,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {card.unassignedCount}
                    </div>
                    unassigned
                  </div>
                </div>
                {pm && (
                  <div
                    style={{
                      marginTop: 10,
                      paddingTop: 10,
                      borderTop: `1px solid ${C.lineSoft}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      color: C.slateMut,
                    }}
                  >
                    <Av name={pm.name} sz={18} />
                    <span>{pm.name} · PM</span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* Reports view */
export interface ReportsViewProps {
  projects: Project[];
  people: Person[];
  costs: CostsByPerson;
  actuals: ActualsByProject;
  rangeStart: string;
  rangeEnd: string;
  role: string;
  setImportOpen: (open: boolean) => void;
  openPerson?: (pid: number) => void;
  currentUserId: number | null;
}

export const ReportsView: FC<ReportsViewProps> = ({
  projects,
  people,
  actuals,
  rangeStart,
  rangeEnd,
  role,
  setImportOpen,
  openPerson,
  currentUserId,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const months = windowMonths(rangeStart, rangeEnd);
  const canSeeCost = role === "admin" || role === "lead" || role === "accountant";

  /* PMs see utilisation only for people on their own projects — mirrors the
     dashboard heatmap scoping.  Admin / Accountant see the full roster. */
  const myProjectIds = new Set(
    role === "pm" && currentUserId
      ? projects.filter((p) => p.pmId === currentUserId).map((p) => p.id)
      : projects.map((p) => p.id),
  );
  const visiblePeople = (
    role === "pm"
      ? people.filter((pp) =>
          projects.some((p) => myProjectIds.has(p.id) && p.members.some((m) => m.pid === pp.id)),
        )
      : people
  ).filter((pp) => !pp.excludeFromPlanning);
  const visibleProjects = (() => {
    const scoped = role === "pm" ? projects.filter((p) => myProjectIds.has(p.id)) : projects;
    const statusRank = (s: string) => (s === "active" ? 1 : s === "archived" ? 99 : 2);
    return (
      scoped
        // Hide archived projects with no period data — keep active/planned regardless.
        .filter((p) => {
          if (p.status !== "archived") return true;
          const v = projectVariance(p);
          return v.plannedH !== 0 || v.actualH !== 0;
        })
        .sort((a, b) => statusRank(a.status) - statusRank(b.status) || a.code.localeCompare(b.code))
    );
  })();

  /* Utilisation runs across the selected period (the same window used for
     variance) — no longer anchored to a calendar year.  Range label shows
     start→end plus month count so the header copy is explicit. */
  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];
  const rangeLabel = firstMonth && lastMonth ? `${firstMonth.label} → ${lastMonth.label}` : "—";
  const yearMonths = months;

  /* Planned vs Actual variance per project */
  function projectVariance(proj: Project) {
    let plannedH = 0;
    let actualH = 0;
    let plannedRev = 0;
    let actualRev = 0;
    for (const m of proj.members) {
      const pers = m.pid ? getPerson(m.pid, people) : null;
      if (pers?.excludeFromPlanning) continue;
      const pObj = { wl: pers?.wl || 100 } as Person;
      for (const ym of months) {
        const raw = m.a[ym.key];
        if (raw) {
          const h = allocToHours(raw, pObj, ym.key, extrasForYmKey(ym.key));
          plannedH += h;
          plannedRev += h * rateForMemberAt(proj, m, ym.key);
        }
        if (m.pid) {
          const h = getActualHours(actuals, proj.id, m.pid, ym.key);
          actualH += h;
          actualRev += h * rateForMemberAt(proj, m, ym.key);
        }
      }
    }
    return {
      plannedH,
      actualH,
      plannedRev,
      actualRev,
      varianceH: actualH - plannedH,
      variancePct: plannedH > 0 ? ((actualH - plannedH) / plannedH) * 100 : 0,
    };
  }

  /* Utilisation per person — yearly aggregate only (single horizontal bar) */
  function personUtil(pp: Person) {
    let capH = 0;
    let plannedH = 0;
    let actualH = 0;
    for (const ym of yearMonths) {
      capH += ((pp.wl || 100) / 100) * whForMonth(ym.key, extrasForYmKey(ym.key));
    }
    for (const proj of projects) {
      /* Time-off projects don't count toward billable utilization — a person
         at 100% vacation shouldn't show as "fully utilized" on the yearly bar. */
      if (proj.isTimeOff) continue;
      for (const m of proj.members.filter((mm) => mm.pid === pp.id)) {
        for (const ym of yearMonths) {
          const raw = m.a[ym.key];
          if (raw) {
            plannedH += allocToHours(raw, pp, ym.key, extrasForYmKey(ym.key));
          }
          actualH += getActualHours(actuals, proj.id, pp.id, ym.key);
        }
      }
    }
    return {
      capH,
      plannedH,
      actualH,
      plannedUtil: capH > 0 ? (plannedH / capH) * 100 : 0,
      actualUtil: capH > 0 ? (actualH / capH) * 100 : 0,
    };
  }

  type VarianceSortKey = "project" | "planned" | "actual" | "variance" | "plannedRev" | "actualRev";
  const varianceSort = useTableSort<Project, VarianceSortKey>({
    project: (p) => p.name.toLowerCase(),
    planned: (p) => projectVariance(p).plannedH,
    actual: (p) => projectVariance(p).actualH,
    variance: (p) => projectVariance(p).variancePct,
    plannedRev: (p) => projectVariance(p).plannedRev,
    actualRev: (p) => projectVariance(p).actualRev,
  });

  type UtilSortKey = "person" | "cap" | "planned" | "actual" | "plannedUtil" | "actualUtil";
  const utilSort = useTableSort<Person, UtilSortKey>({
    person: (pp) => pp.name.toLowerCase(),
    cap: (pp) => personUtil(pp).capH,
    planned: (pp) => personUtil(pp).plannedH,
    actual: (pp) => personUtil(pp).actualH,
    plannedUtil: (pp) => personUtil(pp).plannedUtil,
    actualUtil: (pp) => personUtil(pp).actualUtil,
  });

  function exportReport() {
    const rows: unknown[][] = [
      ["REPORT", `Range: ${rangeStart} → ${rangeEnd}`, `Window: ${months.length} months`],
    ];
    rows.push([]);
    rows.push(["— Project Variance (planned vs actual) —"]);
    rows.push([
      "Code",
      "Name",
      "Status",
      "Planned Hours",
      "Actual Hours",
      "Variance Hours",
      "Variance %",
      "Planned EUR",
      "Actual EUR",
    ]);
    for (const p of projects) {
      if (role === "pm" && !myProjectIds.has(p.id)) continue;
      const v = projectVariance(p);
      rows.push([
        p.code,
        p.name,
        p.status,
        Math.round(v.plannedH),
        Math.round(v.actualH),
        Math.round(v.varianceH),
        Math.round(v.variancePct),
        Math.round(v.plannedRev),
        Math.round(v.actualRev),
      ]);
    }
    rows.push([]);
    if (role !== "accountant") {
      rows.push(["— Person Utilisation —"]);
      rows.push([
        "Name",
        "Capacity Hours",
        "Planned Hours",
        "Actual Hours",
        "Planned Util %",
        "Actual Util %",
      ]);
      for (const pp of people) {
        if (role === "pm" && !visiblePeople.some((v) => v.id === pp.id)) continue;
        const u = personUtil(pp);
        rows.push([
          pp.name,
          Math.round(u.capH),
          Math.round(u.plannedH),
          Math.round(u.actualH),
          Math.round(u.plannedUtil),
          Math.round(u.actualUtil),
        ]);
      }
    }
    downloadCsv(`dolmit-report-${rangeStart}_to_${rangeEnd}.csv`, rows);
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1400, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            Reports
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            Variance ({months.length}-month window)
            {role !== "accountant" && ` · Utilisation (${rangeLabel})`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn
            small
            onClick={() => setImportOpen(true)}
            icon={
              <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M7 13V5m0 0l-3 3m3-3l3 3M2 2h10"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            }
          >
            Import actuals (CSV)
          </Btn>
          <Btn
            small
            onClick={exportReport}
            icon={
              <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M7 1v8m0 0l-3-3m3 3l3-3M2 12h10"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            }
          >
            Export full report
          </Btn>
        </div>
      </div>

      <Card style={{ marginBottom: 16, overflow: "hidden" }}>
        <div
          style={{
            padding: "12px 18px",
            borderBottom: `1px solid ${C.lineSoft}`,
            fontSize: 13,
            fontWeight: 600,
            color: C.navyDeep,
            fontFamily: "'Poppins',system-ui,sans-serif",
          }}
        >
          Project Variance · Planned vs Actual
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <SortableHeader
                label="Project"
                align="left"
                active={varianceSort.isActive("project")}
                indicator={varianceSort.indicator("project")}
                ariaSort={varianceSort.ariaSortFor("project")}
                onSort={() => varianceSort.toggle("project")}
                TH={TH}
              />
              <SortableHeader
                label="Planned h"
                align="right"
                active={varianceSort.isActive("planned")}
                indicator={varianceSort.indicator("planned")}
                ariaSort={varianceSort.ariaSortFor("planned")}
                onSort={() => varianceSort.toggle("planned")}
                TH={TH}
              />
              <SortableHeader
                label="Actual h"
                align="right"
                active={varianceSort.isActive("actual")}
                indicator={varianceSort.indicator("actual")}
                ariaSort={varianceSort.ariaSortFor("actual")}
                onSort={() => varianceSort.toggle("actual")}
                TH={TH}
              />
              <SortableHeader
                label="Variance"
                align="right"
                active={varianceSort.isActive("variance")}
                indicator={varianceSort.indicator("variance")}
                ariaSort={varianceSort.ariaSortFor("variance")}
                onSort={() => varianceSort.toggle("variance")}
                TH={TH}
              />
              <th style={{ ...TH, textAlign: "left", minWidth: 180 }}>Visual</th>
              {canSeeCost && (
                <SortableHeader
                  label="Planned €"
                  align="right"
                  active={varianceSort.isActive("plannedRev")}
                  indicator={varianceSort.indicator("plannedRev")}
                  ariaSort={varianceSort.ariaSortFor("plannedRev")}
                  onSort={() => varianceSort.toggle("plannedRev")}
                  TH={TH}
                />
              )}
              {canSeeCost && (
                <SortableHeader
                  label="Actual €"
                  align="right"
                  active={varianceSort.isActive("actualRev")}
                  indicator={varianceSort.indicator("actualRev")}
                  ariaSort={varianceSort.ariaSortFor("actualRev")}
                  onSort={() => varianceSort.toggle("actualRev")}
                  TH={TH}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {varianceSort.sort(visibleProjects).map((p, i) => {
              const v = projectVariance(p);
              const pctOver = v.plannedH > 0 ? Math.min(100, (v.actualH / v.plannedH) * 100) : 0;
              const over = v.actualH > v.plannedH;
              return (
                <tr key={p.id} style={{ background: i % 2 ? C.surfaceAlt : C.surface }}>
                  <td style={{ ...TD, textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 600, color: C.navyDeep, fontSize: 12.5 }}>
                        {p.name}
                      </span>
                      <SBadge s={p.status} />
                    </div>
                  </td>
                  <td
                    style={{
                      ...TD,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: C.slate,
                    }}
                  >
                    {Math.round(v.plannedH).toLocaleString()}h
                  </td>
                  <td
                    style={{
                      ...TD,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: C.navy,
                      fontWeight: 600,
                    }}
                  >
                    {Math.round(v.actualH).toLocaleString()}h
                  </td>
                  <td
                    style={{
                      ...TD,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 700,
                      color: over ? C.r : v.varianceH < 0 ? C.y : C.g,
                    }}
                  >
                    {v.varianceH >= 0 ? "+" : ""}
                    {Math.round(v.variancePct)}%
                  </td>
                  <td style={{ ...TD, textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, height: 16 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: C.lineSoft,
                          borderRadius: 3,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${pctOver}%`,
                            background: over ? C.r : C.blue,
                            borderRadius: 3,
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            left: `${v.plannedH > 0 ? 100 : 0}%`,
                            top: -2,
                            bottom: -2,
                            width: 2,
                            background: C.slateMut,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          color: C.slateMut,
                          minWidth: 50,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        vs plan
                      </span>
                    </div>
                  </td>
                  {canSeeCost && (
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: C.slate,
                        fontSize: 12,
                      }}
                    >
                      €{Math.round(v.plannedRev).toLocaleString()}
                    </td>
                  )}
                  {canSeeCost && (
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: C.navy,
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      €{Math.round(v.actualRev).toLocaleString()}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Person Utilisation — hidden from Accountant (finance-only role; they
        don't need per-person capacity data). */}
      {role !== "accountant" && (
        <Card style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: "12px 18px",
              borderBottom: `1px solid ${C.lineSoft}`,
              fontSize: 13,
              fontWeight: 600,
              color: C.navyDeep,
              fontFamily: "'Poppins',system-ui,sans-serif",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Person Utilisation · {rangeLabel}</span>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 500,
                color: C.slateMut,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              {months.length} month{months.length === 1 ? "" : "s"}
            </span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <SortableHeader
                  label="Person"
                  align="left"
                  minWidth={180}
                  active={utilSort.isActive("person")}
                  indicator={utilSort.indicator("person")}
                  ariaSort={utilSort.ariaSortFor("person")}
                  onSort={() => utilSort.toggle("person")}
                  TH={TH}
                />
                <SortableHeader
                  label="Cap h"
                  align="right"
                  minWidth={80}
                  active={utilSort.isActive("cap")}
                  indicator={utilSort.indicator("cap")}
                  ariaSort={utilSort.ariaSortFor("cap")}
                  onSort={() => utilSort.toggle("cap")}
                  TH={TH}
                />
                <SortableHeader
                  label="Planned"
                  align="right"
                  minWidth={100}
                  active={utilSort.isActive("planned")}
                  indicator={utilSort.indicator("planned")}
                  ariaSort={utilSort.ariaSortFor("planned")}
                  onSort={() => utilSort.toggle("planned")}
                  TH={TH}
                />
                <SortableHeader
                  label="Actual"
                  align="right"
                  minWidth={100}
                  active={utilSort.isActive("actual")}
                  indicator={utilSort.indicator("actual")}
                  ariaSort={utilSort.ariaSortFor("actual")}
                  onSort={() => utilSort.toggle("actual")}
                  TH={TH}
                />
                <th style={{ ...TH, textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>Period fill rate · {rangeLabel}</span>
                    <span style={{ flex: 1 }} />
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 500,
                        color: C.slateDim,
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      Scale 0–140% · marker = 100% capacity
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {utilSort.sort(visiblePeople).map((pp, i) => {
                const u = personUtil(pp);
                const over = u.actualUtil > 105 || u.plannedUtil > 105;
                /* Scale: 0..140% maps to 0..100% of the bar track; 100% sits at 100/140 */
                const SCALE = 140;
                const planW = (Math.min(SCALE, u.plannedUtil) / SCALE) * 100;
                const actW = (Math.min(SCALE, u.actualUtil) / SCALE) * 100;
                const oneHundred = (100 / SCALE) * 100;
                return (
                  <tr key={pp.id} style={{ background: i % 2 ? C.surfaceAlt : C.surface }}>
                    <td style={{ ...TD, textAlign: "left" }}>
                      <button
                        type="button"
                        onClick={() => openPerson?.(pp.id)}
                        title="Open in Person view"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <Av name={pp.name} sz={24} />
                        <span
                          style={{
                            fontWeight: 600,
                            color: C.navyDeep,
                            fontSize: 12.5,
                            textDecoration: "underline",
                            textDecorationColor: "transparent",
                            textUnderlineOffset: 2,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecorationColor = C.blue;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecorationColor = "transparent";
                          }}
                        >
                          {pp.name}
                        </span>
                      </button>
                    </td>
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: C.slateMut,
                      }}
                    >
                      {Math.round(u.capH)}h
                    </td>
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: C.blue,
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(u.plannedUtil)}%
                    </td>
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: over ? C.r : C.navy,
                        fontWeight: 700,
                      }}
                    >
                      {Math.round(u.actualUtil)}%
                    </td>
                    <td style={{ ...TD, textAlign: "left", padding: "8px 12px" }}>
                      <div
                        style={{
                          position: "relative",
                          height: 18,
                          background: C.lineSoft,
                          borderRadius: 4,
                          overflow: "hidden",
                        }}
                        title={`Planned ${Math.round(u.plannedUtil)}% · Actual ${Math.round(u.actualUtil)}%`}
                      >
                        {/* Planned: ghost bar behind */}
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${planW}%`,
                            background: C.blueTint3,
                          }}
                        />
                        {/* Actual: solid bar overlay (thinner, centered vertically) */}
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 4,
                            bottom: 4,
                            width: `${actW}%`,
                            background: over ? C.r : C.blue,
                            borderRadius: 2,
                          }}
                        />
                        {/* 100% capacity marker */}
                        <div
                          style={{
                            position: "absolute",
                            left: `${oneHundred}%`,
                            top: -2,
                            bottom: -2,
                            width: 1.5,
                            background: C.navyDeep,
                            opacity: 0.55,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: C.slateMut,
                          marginTop: 3,
                          display: "flex",
                          gap: 12,
                        }}
                      >
                        <span>
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: C.blueTint3,
                              marginRight: 4,
                            }}
                          />
                          Planned
                        </span>
                        <span>
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: over ? C.r : C.blue,
                              marginRight: 4,
                            }}
                          />
                          Actual
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};
